import React, { useRef, useEffect, useContext, useState, useMemo } from "react";
import {
	List,
	WindowScroller,
	WindowScrollerChildProps,
} from "react-virtualized";
import {
	SportOffer,
	TournamentOffer,
	EventOffer,
	getEventSortByFavorite,
	FavoritesStore,
} from "@gp/offer";
import ResizeObserver from "resize-observer-polyfill";
import { Sorter } from "@gp/utility";
import { scrollToY } from "@utils";
import { ToggleStore } from "@state/stores/offer/shared/ToggleStore";

export type FlatList = {
	tournament?: TournamentOffer;
	event?: EventOffer;
}[];

/**
 * Function to compare previous with current value
 * @param value value for comparing
 */
export function usePreviousValue(value: number) {
	const ref = useRef<number>();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
}

/**
 * Function to smoothly scroll to a target position within an element
 * - for live offer it takes sport sections for offset calculations
 * - for prematch offer it takes event element for offset calculations
 * @param listRef HTML element's ref used as scrolling container
 * @param sportAbrv sports abbreviation used for determine offset
 * @param _getRowHeight method used for row calculation based ona selected betting type
 * @param callBackOnScrolling callback method to invoke when scrolling is occurring
 * @param durationInMs scrolling duration in milliseconds (default is 1000)
 * @param scrollTriggerPoint distance in pixel to trigger scroll animation (default is 0)
 *
 */
export function useAutoScroll(params: {
	options:
		| {
				offerType: "live";
				_getRowHeight: (params: { index: number }) => number;
				sportAbrv: string | null;
				listRef: React.RefObject<List>;
		  }
		| {
				offerType: "prematch";
				listRef: React.RefObject<List>;
		  };
	callBackOnScrolling: (isScrolling: boolean) => void;
	durationInMs?: number;
	scrollTriggerPoint?: number;
	loaderDisplayPoint?: number;
	sport?: SportOffer;
}) {
	const {
		options,
		durationInMs,
		callBackOnScrolling,
		scrollTriggerPoint,
		loaderDisplayPoint,
		sport,
	} = params;

	const isScrollingProcess = useRef(false);
	const [indexToScrollTo, scrolledToIndex] = useState({
		startIndex: 0,
		stopIndex: 6,
	});

	const autoScroll = () => {
		const scrollableArea = document.querySelector(".l--master");

		if (
			options.offerType == "prematch" &&
			options.listRef.current &&
			indexToScrollTo.startIndex > 0
		) {
			const eventOffset = options.listRef.current?.getOffsetForRow({
				alignment: "center",
				index:
					(indexToScrollTo.startIndex + indexToScrollTo.stopIndex) >>
					1,
			});

			if (scrollableArea) {
				scrollToY(
					scrollableArea,
					eventOffset,
					durationInMs,
					callBackOnScrolling,
					scrollTriggerPoint,
					loaderDisplayPoint
				);
			}
		}

		if (options.offerType == "live") {
			const calculateEventOffset = () => {
				let result = 0;
				let mid =
					(indexToScrollTo.startIndex + indexToScrollTo.stopIndex) >>
					1;
				for (let i = 0; i <= mid; i++) {
					result += options._getRowHeight({
						index: i,
					});
				}
				return result;
			};

			if (scrollableArea) {
				scrollToY(
					scrollableArea,
					calculateEventOffset(),
					durationInMs,
					callBackOnScrolling,
					scrollTriggerPoint,
					loaderDisplayPoint
				);
			}
		}
	};

	if (sport?.isOutright) {
		return {
			indexToScrollTo,
			scrolledToIndex,
			isScrollingProcess,
			autoScroll: () => false,
		};
	}
	return { indexToScrollTo, scrolledToIndex, isScrollingProcess, autoScroll };
}

/**
 * Formats sport offer into list that can be used by ReactVirtualized list
 */
export function useFlatListSport(
	sport: SportOffer,
	favorites?: FavoritesStore,
	collapseStore?: ToggleStore
): FlatList {
	return (
		sport.categories?.reduce((acc: FlatList, cat) => {
			cat.tournaments.forEach((t) => {
				acc.push({
					tournament: t,
				});
				let events = t.events;
				if (favorites && favorites.userFavoriteEventsSet?.size > 0) {
					events = t.events.sort(
						Sorter.sort((a, b) => {
							return Sorter.sort(
								(a1: EventOffer, b1: EventOffer) =>
									getEventSortByFavorite(a1, b1, favorites),

								"startTime"
							)(a, b);
						})
					);
				}
				if (
					sport.isLive
						? collapseStore?.elements.has(t.id + "-live")
						: collapseStore?.elements.has(t.id)
				) {
					events.forEach((e) => {
						acc.push({
							event: e,
						});
					});
				}
			});
			return acc;
		}, []) || []
	);
}

export const WsPropsContext = React.createContext<WindowScrollerChildProps>({
	onChildScroll: ({ scrollTop }) => {},
	registerChild: (element) => {},
	height: 0,
	isScrolling: false,
	scrollLeft: 0,
	scrollTop: 0,
	width: 0,
});

export const useWsProps = () => useContext(WsPropsContext);

const ScrollerDivContext = React.createContext<HTMLElement | null>(null);
const useScrollerDiv = () => useContext(ScrollerDivContext);

/**
 * @param listWrapperElRef Html element that wraps ReactVirtualized List component
 * @param isFirstItem Result is different if it is first virtualized list or n > 1
 * @returns calculated scroll top for virtualized list when there are multiple virtualized lists
 */
export const useMultiListScrollOffset = (
	listWrapperElRef: HTMLElement | null,
	isFirstItem: boolean,
	scrollElement: Element | typeof window = window,
	listHeight?: number
): number => {
	const scrollerDiv = useScrollerDiv();

	const scrollerHeight = listHeight || scrollerDiv?.clientHeight || 0;
	const scrollerOffset = scrollerDiv?.offsetTop || 0;

	const currentElementTop =
		((scrollElement as typeof window).scrollY ||
			(scrollElement as Element).scrollTop ||
			0) +
		scrollerOffset -
		(listWrapperElRef?.offsetTop || 0);

	const isUnderScrollPosition =
		currentElementTop < -1 * window.innerHeight && !isFirstItem;
	const currentScrollTopForList = isUnderScrollPosition
		? scrollerHeight
		: Math.max(currentElementTop, 0);

	return currentScrollTopForList;
};

/**
 * Overrides windowScroller width with  scrollerElement.clientWidth if provided.
 */
export function WindowsScrollerWrapper(props: {
	/** Parent HTMLElement used to set current width, if not provided window width will be used. */
	scrollElement: HTMLElement | null;
	children: JSX.Element;
}) {
	const [width, setWidth] = useState<number>(0);

	//#region observe element width

	const setWidthRef = useRef(() => {});
	setWidthRef.current = () => setWidth(props.scrollElement?.clientWidth || 0);

	const resizeObserver = useMemo(
		() =>
			new ResizeObserver(() => {
				setWidthRef.current();
			}),
		[]
	);

	useEffect(() => {
		if (props.scrollElement != null) {
			resizeObserver.observe(props.scrollElement);
		}
	}, [props.scrollElement]);

	useEffect(() => {
		return () => resizeObserver.disconnect();
	}, []);

	//#endregion observe element width

	return (
		<WindowScroller scrollElement={props.scrollElement || window}>
			{(wsProps: WindowScrollerChildProps) => {
				// Override ws width with scrollElement width since ws is attached to window
				wsProps.width = width > 0 ? width : wsProps.width;

				return (
					<WsPropsContext.Provider value={wsProps}>
						<ScrollerDivContext.Provider
							value={props.scrollElement}
						>
							{props.children}
						</ScrollerDivContext.Provider>
					</WsPropsContext.Provider>
				);
			}}
		</WindowScroller>
	);
}
